import {objectFromEntries} from '@benchling/js-utils';
import reactUpdate from 'immutability-helper';

import {assertAndContinue} from 'Asserts';
import {
  ReplaceLinkStepKey,
  ORDERED_STEPS,
  ReplaceLinkModalReducerState,
  DiffDetails,
} from 'molbio/biointelligent-links/replace-link/constants';
import type {ContainerEditsResult} from 'molbio/dao/SequenceDAO';
import {StepItemStatus} from 'ui-library/StepNav';
import {assertNever} from 'util/Types';

interface UpdateCurrentStepAction {
  type: 'UPDATE_CURRENT_STEP';
  value: ReplaceLinkStepKey;
}

interface SetSelectedContainerPolymerIdsAction {
  type: 'SET_SELECTED_CONTAINER_POLYMER_IDS';
  value: Array<string>;
}

interface SetDiffDetailsAction {
  type: 'SET_DIFF_DETAILS';
  value: DiffDetails | null;
}

interface ToggleSidebarExpandedAction {
  type: 'TOGGLE_SIDEBAR_EXPANDED';
}

interface ToggleConfirmedContainerIdsAction {
  type: 'TOGGLE_CONFIRMED_CONTAINER_IDS';
  ids: Array<string>;
  isSelected: boolean;
}

interface SetContainerEditEffectsAction {
  type: 'SET_CONTAINER_EDIT_EFFECTS';
  value: Record<string, ContainerEditsResult>;
}

interface SetLoadingEditEffectsAction {
  type: 'SET_LOADING_EDIT_EFFECTS';
  value: boolean;
}

export type ReplaceLinkAction =
  | UpdateCurrentStepAction
  | SetSelectedContainerPolymerIdsAction
  | SetDiffDetailsAction
  | ToggleSidebarExpandedAction
  | ToggleConfirmedContainerIdsAction
  | SetContainerEditEffectsAction
  | SetLoadingEditEffectsAction;

export default function replaceLinkReducer(
  state: ReplaceLinkModalReducerState,
  action: ReplaceLinkAction
): ReplaceLinkModalReducerState {
  switch (action.type) {
    case 'UPDATE_CURRENT_STEP': {
      const indexOfNewStep = ORDERED_STEPS.findIndex((step) => step === action.value);
      assertAndContinue(indexOfNewStep !== -1, `Got unexpected step: ${action.value}`);
      return reactUpdate(state, {
        currentStepKey: {$set: action.value},
        statusByStep: {
          $set: objectFromEntries(
            ORDERED_STEPS.map(
              (step: ReplaceLinkStepKey, index: number): [ReplaceLinkStepKey, StepItemStatus] => [
                step,
                index < indexOfNewStep ? StepItemStatus.DONE : StepItemStatus.UNVISITED,
              ]
            )
          ) as {[key in ReplaceLinkStepKey]: StepItemStatus},
        },
      });
    }
    case 'SET_SELECTED_CONTAINER_POLYMER_IDS': {
      return reactUpdate(state, {
        selectedContainerPolymerIds: {$set: action.value},
      });
    }
    case 'SET_DIFF_DETAILS': {
      return reactUpdate(state, {
        diffDetails: {$set: action.value},
      });
    }
    case 'TOGGLE_SIDEBAR_EXPANDED': {
      return reactUpdate(state, {
        sidebarExpanded: {$set: !state.sidebarExpanded},
      });
    }
    case 'TOGGLE_CONFIRMED_CONTAINER_IDS': {
      const blocked = new Set(
        Object.entries(state.containerEditResultsByPolymerId)
          .filter(([, r]) => r.type === 'blocked')
          .map(([id]) => id)
      );
      const currentIds = new Set(state.confirmedContainerIds);
      if (action.isSelected) {
        action.ids.forEach((id) => {
          if (!blocked.has(id)) {
            currentIds.add(id);
          }
        });
      } else {
        action.ids.forEach((id) => currentIds.delete(id));
      }
      return reactUpdate(state, {
        confirmedContainerIds: {$set: Array.from(currentIds)},
      });
    }
    case 'SET_CONTAINER_EDIT_EFFECTS': {
      const confirmedContainerIds = Object.entries(action.value)
        .filter(([, result]) => result.type !== 'blocked')
        .map(([id]) => id);
      return reactUpdate(state, {
        containerEditResultsByPolymerId: {$set: action.value},
        confirmedContainerIds: {$set: confirmedContainerIds},
        isLoadingEditEffects: {$set: false},
      });
    }
    case 'SET_LOADING_EDIT_EFFECTS': {
      if (action.value) {
        return reactUpdate(state, {
          isLoadingEditEffects: {$set: true},
          containerEditResultsByPolymerId: {$set: {}},
          confirmedContainerIds: {$set: []},
        });
      }
      return reactUpdate(state, {
        isLoadingEditEffects: {$set: false},
      });
    }
    default:
      assertNever(action);
      return state;
  }
}
